import "./style.scss";
import { isDefined } from "@clipboard-health/util-ts";
import { IonSpinner } from "@ionic/react";
import { IShiftSlot } from "@src/app/components/workWithFriends/interface";
import { useDefinedAgent } from "@src/app/store/helperHooks";
import { useToast } from "@src/appV2/lib";
import { BookabilityAttributes } from "@src/appV2/OpenShifts/ShiftAction";
import { ShiftPeriod } from "@src/appV2/Shifts/Shift/types";
import { SEARCH_MODE } from "@src/constants";
import { Shift } from "@src/lib/interface";
import { sortBy } from "lodash";
import { FC, useCallback, useEffect, useState } from "react";

import { allowFacilityDetailsSearchModes } from "./constants";
import { HcfProfileSource } from "./model";
import { isNonConflictingShift, removeConflictingShifts } from "../../utils/shiftHelper";
import { addAgentActivityLog, fetchInstantBookShifts } from "../api";
import { HCP_ACTIONS } from "../constants";
import { ShiftListGroupState } from "../model";
import { ShiftItem } from "../shiftItem";

type FacilityShiftItemProps = {
  day: string;
  shift: Shift;
  shifts: ShiftListGroupState;
  goToDocumentsPage: (shift: Shift) => void;
  getFacilityShifts: () => Promise<any>;
  setClickedInterestInShift: (value: boolean) => void;
  isHcpInstantPayEnabled?: boolean;
  pushNotification: { [key in string]: boolean };
  searchMode?: SEARCH_MODE;
  availableShiftIds?: string[];
  filterDistance?: number;
  filterDays?: number;
  hcfProfileSource?: HcfProfileSource;
  shiftSlots?: IShiftSlot[];
  allowedAgentReq?: string[];
  onFacilityDetailsClick?: (
    shift: Shift,
    isInstantBook: boolean,
    displayExtraTimePayCard?: boolean
  ) => void;
  isSingleSourceOfBookabilityEnabled: boolean;
  shiftBookability: BookabilityAttributes;
};
const FacilityShiftItem: FC<FacilityShiftItemProps> = ({
  day,
  shift,
  shifts,
  goToDocumentsPage,
  getFacilityShifts,
  setClickedInterestInShift,
  isHcpInstantPayEnabled,
  pushNotification,
  searchMode,
  availableShiftIds,
  filterDistance,
  filterDays,
  hcfProfileSource,
  shiftSlots,
  allowedAgentReq,
  onFacilityDetailsClick,
  shiftBookability,
  isSingleSourceOfBookabilityEnabled,
}) => {
  const { showErrorToast } = useToast();
  const agent = useDefinedAgent();
  const [shiftName, setShiftName] = useState<ShiftPeriod | undefined | null>(null);

  // Given the probability of the FF and a random probability , this decides whether to
  // make the api call to the Main-API or the BFF-API

  useEffect(() => {
    setShiftName(shift.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shiftValidatorBeforeSubmission = (shift: Shift) => {
    // Already assigned shifts doesn't appear on same shift type. So only checking on open shifts.
    if (
      isNonConflictingShift(
        [
          ...shifts.assigned,
          ...shifts.open.filter(
            ({ agentId, interested }) =>
              agentId === agent.userId || interested?.includes(String(agent.userId))
          ),
        ],
        shift
      )
    ) {
      return true;
    }
    showErrorToast(
      `Unable to book shift, it looks like you're already working at that time! If there's a mistake, please contact support for help`
    );
    return false;
  };

  // Instant Book check
  const [isLoadingCheckInstantBook, setIsLoadingCheckInstantBook] = useState<boolean>(true);
  const [instantBookShifts, setInstantBookShifts] = useState<Shift[]>([]);
  const [isShiftLoaded, setIsShiftLoaded] = useState<boolean>(false);
  const [sortedShifts, setSortedShifts] = useState<Shift[]>([]);

  const getInstantBookShifts = useCallback(async () => {
    if (day && shiftName) {
      setIsLoadingCheckInstantBook(true);
      const instantbookShiftsLoaded = await fetchInstantBookShifts(day);

      const instantbookShifts = (instantbookShiftsLoaded || []).filter(
        (shift) => shiftName === shift.name
      );
      setInstantBookShifts(instantbookShifts);
      setIsLoadingCheckInstantBook(false);
      setIsShiftLoaded(true);
    }
  }, [day, shiftName]);

  useEffect(() => {
    getInstantBookShifts();
  }, [getInstantBookShifts]);

  // Check if shifts loaded
  useEffect(() => {
    setSortedShifts(
      sortBy(removeConflictingShifts(shifts.open, shifts.assigned), [
        "facility.distance",
        "start",
      ]) as Shift[]
    );
  }, [shifts.open, shifts.assigned]);

  // Logger
  const logAgentActivity = useCallback(
    (shiftsToLog: any[], action: string, shiftId = null) => {
      if (agent.userId && day && shiftName && !isLoadingCheckInstantBook && isShiftLoaded) {
        const agentActivityLog = {
          day: day,
          shiftId,
          timeSlot: shiftName.toUpperCase(),
          agentId: agent.userId,
          shifts: shiftsToLog,
          action,
        };
        addAgentActivityLog(agentActivityLog);
      }
    },
    [agent.userId, day, shiftName, isLoadingCheckInstantBook, isShiftLoaded]
  );

  useEffect(() => {
    if (!isShiftLoaded && isLoadingCheckInstantBook) {
      return;
    }
    let currentIndex = 1;
    const shiftsToLog = [...shifts.open, ...shifts.assigned].reduce((filtered: Shift[], option) => {
      if (isDefined(option)) {
        const shift = {
          ...option,
          position: currentIndex,
          isInstantBook: instantBookShifts.findIndex((a: Shift) => a.shiftId === option._id) > -1,
        };
        filtered.push(shift);
        currentIndex = currentIndex + 1;
      }
      return filtered;
    }, []);
    logAgentActivity(shiftsToLog, HCP_ACTIONS.TIME_SLOT_CLICK);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShiftLoaded, isLoadingCheckInstantBook]);

  const filterShiftsToLog = (shiftId = null) => {
    //Getting Mark Interest Shifts and filtering the null items
    const markInterestShifts = sortedShifts.filter((el) => {
      return isDefined(el) && instantBookShifts.findIndex((k: Shift) => k.shiftId === el._id) < 0;
    });
    // Combining mark and self claim shifts
    const combinedShifts = instantBookShifts.concat(markInterestShifts);
    //Using to set the position of the shift
    let currentIndex = 1;
    //filtering the mark interest shifts and is instant check
    const shiftsToLog = combinedShifts.reduce((filtered: Shift[], option) => {
      if (
        option.interested!.indexOf(String(agent?.userId)) < 0 &&
        !option["isClaimed"] &&
        shiftId !== option.shiftId
      ) {
        const isInstantBook =
          instantBookShifts.findIndex((instantShift) => instantShift.shiftId === option.shiftId) >
          -1;
        const shift = {
          ...option,
          type: isInstantBook ? HCP_ACTIONS.SELF_CLAIM : HCP_ACTIONS.MARK_INTEREST,
          position: currentIndex,
          isInstantBook,
        };
        filtered.push(shift);
      }
      currentIndex = currentIndex + 1;
      return filtered;
    }, []);

    return shiftsToLog;
  };
  const shiftActivityLog = (shiftId) => {
    const shiftsToLog = filterShiftsToLog(shiftId);
    return { shifts: shiftsToLog };
  };
  const handleShiftActivityLog = (shiftId, action) => {
    const shiftsToLog = filterShiftsToLog(shiftId);
    //Recording Activity
    logAgentActivity(shiftsToLog, action, shiftId);
  };

  const facilityDetailsPageIsEnabled =
    isDefined(searchMode) && allowFacilityDetailsSearchModes.includes(searchMode);

  if (!isLoadingCheckInstantBook && isShiftLoaded) {
    return (
      <ShiftItem
        shift={shift}
        isInstantBookingShift={instantBookShifts.some(
          (instantShift) => instantShift.shiftId === shift.shiftId
        )}
        shiftName={shiftName as string}
        goToDocumentsPage={goToDocumentsPage}
        updateShiftList={getFacilityShifts}
        setClickedInterestInShift={setClickedInterestInShift}
        isHcpInstantPayEnabled={isHcpInstantPayEnabled}
        handleShiftActivityLog={handleShiftActivityLog}
        validator={shiftValidatorBeforeSubmission}
        pushNotification={pushNotification}
        shiftActivityLog={shiftActivityLog}
        showFacilityDetails={facilityDetailsPageIsEnabled}
        loggerSearchMode={"facility"}
        availableShiftIds={availableShiftIds}
        searchMode={searchMode}
        filterDistance={filterDistance}
        filterDays={filterDays}
        hcfProfileSource={hcfProfileSource}
        shiftSlots={shiftSlots}
        allowedAgentReq={allowedAgentReq}
        onFacilityDetailsClick={onFacilityDetailsClick}
        shiftBookability={shiftBookability}
        isSingleSourceOfBookabilityEnabled={isSingleSourceOfBookabilityEnabled}
      />
    );
  }
  return (
    <div
      style={{
        textAlign: "center",
        padding: "4em 4em",
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <IonSpinner />
    </div>
  );
};
export { FacilityShiftItem };
